import { Protocol } from '@studio/models/video';

/** Language constants. */
export const Lang = Object.freeze({
  protocol: {
    [Protocol.NDI]: 'NDI',
    [Protocol.RTSP]: 'RTSP',
    [Protocol.RTSPLT]: 'RTSP Lite',
    [Protocol.RTSPT]: 'RTSP/TCP',
    [Protocol.Local]: 'Local',
    [Protocol.IRIS]: 'Iris'
  } satisfies Record<Protocol, string>
});

// Per ai.knowledge rule investigate-before-adding: this module already holds video-domain
// constants (Lang). The Local and NDI editors share the encoder tables here, and non-React
// modules (protobuf/device.ts, mediadevices/pipeline.ts) import DefaultBitrate without pulling
// the full React component tree into jest's module graph.
const encodeProfileMatrix = Object.freeze({
  '2160': { h264: 16.0, vp8: 10.0, vp9: 7.0, av1: 6.0 },
  '1080': { h264: 8.5, vp8: 5.5, vp9: 4.0, av1: 3.5 },
  '720': { h264: 3.5, vp8: 2.5, vp9: 1.8, av1: 1.7 },
  '540': { h264: 1.8, vp8: 1.5, vp9: 1.1, av1: 1.0 },
  '360': { h264: 1.0, vp8: 0.75, vp9: 0.5, av1: 0.5 }
});

const encoderProfileMultiplierMatrix = Object.freeze({
  'Low': 1.0,
  'Medium': 1.5,
  'High': 2.5,
  'Very High': 3.5
});

export const DefaultBitrate =
  encodeProfileMatrix['1080'].vp8 * encoderProfileMultiplierMatrix.High * 100_000;

/** Encoder profile names, in the order the editors offer them. */
export const encoderProfiles = Object.freeze(Object.keys(encoderProfileMultiplierMatrix));

// Ties keep the later item.
const nearest = <T>(items: readonly T[], distance: (item: T) => number): T =>
  items.reduce((a, b) => (distance(a) < distance(b) ? a : b));

/** vp8 bitrate for `profile` at the table height closest to `height`. */
export const calculateNextBitrate = (profile: string, height: number): number => {
  // A profile name read from settings may be outside the table; it keeps a 1.0 multiplier.
  const multipliers: Readonly<Record<string, number>> = encoderProfileMultiplierMatrix;
  const rows = Object.entries(encodeProfileMatrix);
  const [, closest] = nearest(rows, ([row]) => Math.abs(Number.parseInt(row, 10) - height));

  return closest.vp8 * (multipliers[profile] ?? 1.0) * 100_000;
};

/** Match the encoder profile to the closest bitrate for vp8 at `height`. */
export const closestEncoderProfile = (bitrate: number, height: number): string =>
  nearest(encoderProfiles, (profile) => Math.abs(calculateNextBitrate(profile, height) - bitrate));
